/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Alert, SubmitButton } from 'omakase';
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { FormActions } from '@tock/shared/components/forms/FormActions';
import { FormEmailField } from 'components/inputs/FormEmailField';
import { FormPhoneCountryCodeField, FormPhoneField, FormTextField, Grid } from 'ui';
import { FormValidatePasswordField } from 'components/inputs/FormValidatePasswordField';
import { IWithContextProps, core, withContext } from '@tock/coretypes';
import { SignupValues } from 'components/sign-in/Signin';
import { SmsSubscribeConfirmationModal } from 'components/modal/SmsSubscribeConfirmationModal';
import { WithPhoneValidationProps, withPhoneValidation } from 'hooks/usePhoneValidation';
import {
  isEmail,
  isGoodPassword,
  requireFields,
  validateFirstLastNameIfPresent,
} from '@tock/shared/utils/FormValidators';
import { isSmsPhoneStatusTypeOptOutHard } from 'utils/smsUtils';
import BaseComponent from '@tock/shared/components/BaseComponent';
import FormIsoCountryCodeField from 'ui/FormIsoCountryCodeField/FormIsoCountryCodeField';
import TurnstileValidator, {
  TokenRefreshHandle,
  isTurnstileActive,
} from 'forms/TurnstileValidator';
import _ from 'lodash';

interface State {
  isSmsConfirmationModalOpen: boolean;
}

interface SignupFormProps {
  detectedLocation?: core.DetectedLocation;
  email?: string;
  guest?: core.Patron;
  disableEmail?: boolean;
  isAccountActivation?: boolean;
  submitText?: string;
  layout?: 'SINGLE_COLUMN' | 'TWO_COLUMN';
  onSubmit(values: SignupValues, requireChallengeResponse: boolean): Promise<any>;
}

type Props = SignupFormProps & IWithContextProps & WithPhoneValidationProps;

// This is factored this way for testability, and exported for test only.
export function validate(values: SignupValues, requireChallengeResponse: boolean) {
  const errors: FormikErrors<SignupValues> = requireFields(
    values,
    'email',
    'password',
    'firstName',
    'lastName',
    'phone'
  );
  if (!isEmail(values.email)) {
    errors.email = 'Please enter a valid email';
  }

  const nameErrors = validateFirstLastNameIfPresent(values);

  if (!isGoodPassword(values.password)) {
    errors.password = 'Password must be at least 8 characters in length.';
  }

  if (values.password.length > 256) {
    errors.password = 'Password must be less than 256 characters in length.';
  }

  if (requireChallengeResponse && !values.challengeResponse) {
    errors.challengeResponse = 'Please complete captcha.';
  }

  return { ...errors, ...nameErrors };
}

class SignupForm extends BaseComponent<Props, State> {
  static defaultProps: any;

  emailInput: React.RefObject<HTMLInputElement>;
  passwordInput: React.RefObject<HTMLInputElement>;
  turnstileValidatorRef: React.RefObject<TokenRefreshHandle>;
  formikRef: React.RefObject<FormikProps<SignupValues>> = React.createRef();
  state: State = { isSmsConfirmationModalOpen: false };

  constructor(props: Props) {
    super(props);
    this.emailInput = React.createRef();
    this.passwordInput = React.createRef();
    this.turnstileValidatorRef = React.createRef();
  }

  handleAdvanceToFieldAfterEmail() {
    if (this.passwordInput.current) {
      this.passwordInput.current.focus();
    }
  }

  getInitialValues() {
    const { guest, detectedLocation, email } = this.props;

    const phoneCountryCode =
      guest?.phoneCountryCode || detectedLocation?.phoneCountryCode || '+1 US';
    const isoCountryCode = guest?.isoCountryCode || detectedLocation?.isoCountryCode || 'US';

    return {
      phoneCountryCode,
      email: guest?.email || email || '',
      isoCountryCode,
      firstName: guest?.firstName || '',
      lastName: guest?.lastName || '',
      phone: guest?.phone || '',
      password: '',
      zipCode: '',
      challengeResponse: '',
      smsPhoneConsentStatus: 'NOT_SPECIFIED' as core.SmsPhone.StatusType,
    };
  }

  validateInternal = (values: SignupValues) => {
    return validate(values, this.getRequireChallengeResponse());
  };

  async handleSubmit(values: SignupValues, actions: FormikHelpers<SignupValues>) {
    actions.setStatus('');

    try {
      await this.props.validatePhone(values);

      const { onSubmit } = this.props;
      onSubmit(values, this.getRequireChallengeResponse())
        .then(() => {
          actions.setSubmitting(false);
        })
        .catch((error) => {
          actions.setStatus(error._error);
          actions.setSubmitting(false);
          this.turnstileValidatorRef.current?.refreshToken();
        });
    } catch (msg) {
      actions.setStatus(msg);
    }
  }

  getRequireChallengeResponse = () => {
    return isTurnstileActive(this.props.context.isFeatureEnabled('TURNSTILE_RENDER_CLIENT'));
  };

  showSmsConfirmationModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.setState({ isSmsConfirmationModalOpen: true });
  };

  handleSmsConfirmationModalResult = async (status: core.SmsPhone.StatusType) => {
    // Set the form field value
    this.formikRef.current?.setFieldValue('smsPhoneConsentStatus', status);
    // Close the modal
    this.setState({ isSmsConfirmationModalOpen: false });
    // Submit the form after the user has made their choice in the modal
    await this.formikRef.current?.submitForm();
  };

  render() {
    const {
      context: { isFeatureEnabled },
      disableEmail,
      guest,
      submitText,
      layout,
      isAccountActivation,
    } = this.props;
    const hasTwilioDirectBrandUpdatesFlag =
      isFeatureEnabled('TWILIO_DIRECT_BRAND_UPDATES') &&
      isFeatureEnabled('TWILIO_DIRECT_BRAND_DATA_ONLY');
    const phoneIsHardOptOut = guest
      ? isSmsPhoneStatusTypeOptOutHard(guest.smsPhoneConsentStatus)
      : false;

    const sm = layout === 'SINGLE_COLUMN' ? 12 : layout === 'TWO_COLUMN' ? 6 : 12;

    return (
      <>
        <Formik<SignupValues>
          innerRef={this.formikRef}
          initialValues={this.getInitialValues()}
          onSubmit={this.handleSubmit}
          validate={this.validateInternal}
          enableReinitialize
        >
          {(props: FormikProps<SignupValues>) => (
            <Form data-testid="signup-form">
              {hasTwilioDirectBrandUpdatesFlag && (
                <FormTextField
                  name="smsPhoneConsentStatus"
                  id="smsPhoneConsentStatus"
                  inputProps={{
                    'data-testid': 'sms-phone-consent-status-hidden-input',
                  }}
                  type="hidden"
                />
              )}
              <Grid container spacing={1}>
                {!_.isEmpty(props.errors) && props.submitCount > 0 && !props.status && (
                  <Grid item xs={12}>
                    <Alert severity="error">Oops there are errors on the form</Alert>
                  </Grid>
                )}
                {props.status && (
                  <Grid item xs={12}>
                    <Alert severity="error">{props.status}</Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={sm}>
                  <FormEmailField
                    fullWidth
                    disabled={!!disableEmail || !!isAccountActivation}
                    inputRef={this.emailInput}
                    inputProps={{
                      'data-testid': 'email-field',
                      autoComplete: 'email',
                    }}
                    onUseSuggestion={this.handleAdvanceToFieldAfterEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={sm}>
                  <FormValidatePasswordField
                    fullWidth
                    inputRef={this.passwordInput}
                    inputProps={{
                      'data-testid': 'patron-password-input',
                      autoComplete: 'new-password',
                    }}
                  />
                </Grid>
                {!isAccountActivation ? (
                  <>
                    <Grid item xs={12} sm={sm}>
                      <FormTextField
                        fullWidth
                        name="firstName"
                        id="firstName"
                        label="First name"
                        inputProps={{
                          'data-testid': 'patron-first-name-input',
                          autoComplete: 'given-name',
                        }}
                        placeholder="First name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={sm}>
                      <FormTextField
                        fullWidth
                        name="lastName"
                        id="lastName"
                        label="Last name"
                        inputProps={{
                          'data-testid': 'patron-last-name-input',
                          autoComplete: 'family-name',
                        }}
                        placeholder="Last name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={sm}>
                      <FormPhoneCountryCodeField fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={sm}>
                      <FormPhoneField
                        fullWidth
                        inputProps={{
                          'data-testid': 'patron-phone-input',
                          autoComplete: 'tel-national',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={sm}>
                      <FormIsoCountryCodeField />
                    </Grid>
                  </>
                ) : null}
                <Grid item xs={12} sm={sm}>
                  <FormTextField
                    fullWidth
                    name="zipCode"
                    id="zipCode"
                    label="Zip code"
                    inputProps={{
                      'data-testid': 'zip-code',
                      autoComplete: 'postal-code',
                    }}
                    placeholder="ZIP/Postal code (optional)"
                  />
                </Grid>
                {this.getRequireChallengeResponse() && (
                  <Grid item xs={12}>
                    <TurnstileValidator
                      onReceiveChallengeResponse={(challengeResponse) =>
                        props.setFieldValue('challengeResponse', challengeResponse)
                      }
                      ref={this.turnstileValidatorRef}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormActions>
                    {hasTwilioDirectBrandUpdatesFlag ? (
                      <SubmitButton
                        data-testid="signup-button"
                        type="button" // Override to button to manage SMS modal flow
                        variant="primary"
                        size="large"
                        fullWidth
                        isSubmitting={props.isSubmitting}
                        onClick={this.showSmsConfirmationModal}
                      >
                        {_.isEmpty(submitText) ? 'Sign up' : submitText}
                      </SubmitButton>
                    ) : (
                      <SubmitButton
                        data-testid="signup-button"
                        variant="primary"
                        size="large"
                        fullWidth
                        isSubmitting={props.isSubmitting}
                      >
                        {_.isEmpty(submitText) ? 'Sign up' : submitText}
                      </SubmitButton>
                    )}
                  </FormActions>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
        {hasTwilioDirectBrandUpdatesFlag && (
          <SmsSubscribeConfirmationModal
            curPatronHardUnsubscribed={phoneIsHardOptOut}
            isOpen={this.state.isSmsConfirmationModalOpen}
            onClose={this.handleSmsConfirmationModalResult}
            pageType="Signup"
          />
        )}
      </>
    );
  }
}

SignupForm.defaultProps = {
  email: '',
};
export default withPhoneValidation(withContext(SignupForm));
